import React, { useEffect, useRef, useState } from 'react';
import { Column } from '@antv/g2plot';
import { Switch } from 'antd';
import styles from './styles/cash-flow-chart.module.scss';

const CashFlowChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [showInflows, setShowInflows] = useState(true);
  const [showOutflows, setShowOutflows] = useState(true);
  const [containerWidth, setContainerWidth] = useState(550);
  const [containerHeight, setContainerHeight] = useState(320);

  // Format function for month labels (YYYY-MM to short month name)
  const formatMonthYear = (yyyyMm) => {
    if (!yyyyMm) return '';
    const [year, month] = yyyyMm.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { 
      month: 'short',
    });
  };

  useEffect(() => {
    // Function to get container width - observe the wrapper (card) that's in the grid
    const updateWidth = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const width = rect.width || wrapperRef.current.offsetWidth || 550;
        setContainerWidth(Math.max(width, 300));
      }
    };

    // Initial width calculation with a small delay to ensure DOM is ready
    const timeout = setTimeout(updateWidth, 100);

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const width = entry.contentRect.width || entry.target.offsetWidth || 550;
        setContainerWidth(Math.max(width, 300));
      }
    });

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    // Poll during sidebar transitions using requestAnimationFrame
    let rafId = null;
    let lastWidth = 0;
    const pollWidth = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const currentWidth = rect.width || wrapperRef.current.offsetWidth || 550;
        if (Math.abs(currentWidth - lastWidth) > 1) {
          setContainerWidth(Math.max(currentWidth, 300));
          lastWidth = currentWidth;
        }
        rafId = requestAnimationFrame(pollWidth);
      }
    };

    // Start polling
    rafId = requestAnimationFrame(pollWidth);

    // Also listen to window resize as fallback
    window.addEventListener('resize', updateWidth);

    // Watch for sidebar class changes (for transition detection)
    const sidebarObserver = new MutationObserver(() => {
      updateWidth();
    });

    // Find sidebar element
    const sidebarElement = document.querySelector('[class*="sidebar"]');
    if (sidebarElement) {
      sidebarObserver.observe(sidebarElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    return () => {
      clearTimeout(timeout);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      sidebarObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    // Use provided data from backend (no mock data fallback)
    const rawData = data || [];

    // Transform data for chart based on toggle states
    const chartData = [];
    rawData.forEach(item => {
      const monthLabel = formatMonthYear(item.month);
      const chartItem = { month: monthLabel };
      
      if (showInflows) {
        chartItem.inflow = item.inflow || 0;
      }
      if (showOutflows) {
        chartItem.outflow = item.outflow || 0;
      }
      
      chartData.push(chartItem);
    });

    // Prepare series data for g2plot (needs to be in long format for grouped bar)
    const seriesData = [];
    chartData.forEach(item => {
      if (showInflows && item.inflow !== undefined) {
        seriesData.push({
          month: item.month,
          type: 'inflow',
          value: item.inflow
        });
      }
      if (showOutflows && item.outflow !== undefined) {
        seriesData.push({
          month: item.month,
          type: 'outflow',
          value: item.outflow
        });
      }
    });

    if (chartRef.current && containerWidth > 0 && seriesData.length > 0) {
      // If chart instance exists, update it instead of recreating
      if (chartInstanceRef.current) {
        chartInstanceRef.current.update({
          data: seriesData,
          width: containerWidth,
          height: containerHeight,
        });
      } else {
        // Create new chart instance
        chartInstanceRef.current = new Column(chartRef.current, {
          data: seriesData,
          xField: 'month',
          yField: 'value',
          seriesField: 'type',
          isGroup: true,
          color: ['#3b82f6', '#f97316'], // Blue for inflow, Orange for outflow
          tooltip: {
            formatter: (datum) => {
              const name = datum.type === 'inflow' ? 'Inflow' : 'Outflow';
              return { name, value: datum.value ? datum.value.toLocaleString() : '0' };
            },
          },
          label: {
            position: 'top',
            formatter: (datum) => {
              // Don't show label if value is 0
              if (!datum.value || datum.value === 0) {
                return '';
              }
              return datum.value.toLocaleString();
            },
            style: {
              fontSize: 10,
              fill: '#6b7280',
              fontWeight: 500,
            },
          },
          yAxis: {
            min: 0,
            grid: {
              line: {
                style: {
                  stroke: '#e5e7eb',
                  lineWidth: 1,
                },
              },
            },
          },
          xAxis: {
            label: {
              style: {
                fill: '#6b7280',
                fontSize: 12,
                fontFamily: 'Inter, SF Pro Text, sans-serif',
              },
            },
            line: null,
            tickLine: null,
          },
          height: containerHeight,
          width: containerWidth,
          legend: {
            visible: false, // Hide legend since we have toggles
          },
        });

        chartInstanceRef.current.render();
      }
    }

    // Cleanup function
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [data, showInflows, showOutflows, containerWidth, containerHeight]);

  return (
    <div className={styles.card} ref={wrapperRef}>
      <div className={styles.header}>
        <h3 className={styles.title}>Cashflow Over Time</h3>
        <div className={styles.toggles}>
          <div className={styles.toggleItem}>
            <span className={styles.label}>Inflows</span>
            <Switch 
              checked={showInflows}
              onChange={setShowInflows}
              size="small"
            />
          </div>
          <div className={styles.toggleItem}>
            <span className={styles.label}>Outflows</span>
            <Switch 
              checked={showOutflows}
              onChange={setShowOutflows}
              size="small"
            />
          </div>
        </div>
      </div>
      
      <div className={styles.chartContainer} ref={containerRef}>
        <div ref={chartRef} style={{ width: '100%', height: `${containerHeight}px` }} />
      </div>
    </div>
  );
};

export default CashFlowChart;
